import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  changePassword,
  checkLogin,
  getUser,
  login,
  resetPassword,
  setFullWalkthroughFlag,
  setSingleWalkthroughFlag,
  getMePublishers,
  getUserClassrooms,
  getUserConfigurationsData,
  getUserCourses,
  getSystemConfigs,
  postAddSchoolToUser,
  postAddSchoolCode,
  postUserFirstStep,
  postAddCourseToUser,
  postAddClassroomToUser,
  postResendEmail,
  postLegalConditions,
  postUserFeedbackPool,
  patchUser,
  recoverPassword,
  unlinkWithGoogle,
  patchUserPromotionTerms,
  sendLogout,
  acceptEdubookLegalConditions,
  acceptPublisherLegalConditions,
  patchPublisherLegalConditions,
  putChangeUserFlag,
  getTutorIaCourses,
  postTutorIaBookId,
  patchUserPersonalData,
} from 'api/user'
import { LOGIN } from 'config/login'
import { hideLoader } from 'store/actions/drawer.actions'
import { TYPES_ALERTS } from 'utils/constants'
import { useSnackBarFeedback } from 'context/snackbarFeedback'
import { STATUS_FLAGS } from 'utils/constants/flags'
import { STUDENT_KEYS } from 'hooks/student'

export const USER_KEYS = {
  CHECK_LOGIN: 'CHECK_LOGIN',
  GET_USER: 'GET_USER',
  SYSTEM_CONFIG: 'SYSTEM_CONFIG',
  TUTOR_IA_SUBJECTS: 'TUTOR_IA_SUBJECTS',
  USER_CLASSROOMS: 'USER_CLASSROOMS',
  USER_CONFIGURATIONS: 'USER_CONFIGURATIONS',
  USER_COURSES: 'USER_COURSES',
  USER_NEW_PASSWORD: 'USER_NEW_PASSWORD',
  USER_PUBLISHER: 'USER_PUBLISHER',
}

export const useSetFullWalkthroughFlag = (valueFlag) => {
  const queryClient = useQueryClient()
  return useMutation(() => setFullWalkthroughFlag(valueFlag), {
    onSuccess: () => {
      queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => ({
        ...oldData,
        userFlags: {
          ...oldData.userFlags,
          FULL_WALKTHROUGH: STATUS_FLAGS.FALSE,
          SINGLE_WALKTHROUGH: STATUS_FLAGS.FALSE,
        },
      }))
      //WIP: change cache /me as relative
      // queryClient.invalidateQueries({ queryKey: [USER_KEYS.GET_USER] })
    },
  })
}

export const useSetSingleWalkthroughFlag = (valueFlag) => {
  const queryClient = useQueryClient()
  return useMutation(() => setSingleWalkthroughFlag(valueFlag), {
    onSuccess: () => {
      queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => ({
        ...oldData,
        userFlags: {
          ...oldData.userFlags,
          SINGLE_WALKTHROUGH: STATUS_FLAGS.FALSE,
        },
      }))
      //WIP: change cache /me as relative
      // queryClient.invalidateQueries({ queryKey: [USER_KEYS.GET_USER] })
    },
  })
}

export const useLogin = () => {
  return useMutation(() =>
    login({
      licenseCode: LOGIN.LICENSE,
      password: LOGIN.PASSWORD,
      user: LOGIN.USER,
    })
  )
}

export const useCheckLogin = (enabled) => {
  const dispatch = useDispatch()
  const hideLoaderToLogin = () => {
    dispatch(hideLoader(true))
    return checkLogin()
  }
  return useQuery([USER_KEYS.CHECK_LOGIN], hideLoaderToLogin, {
    enabled: !!enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    refetchInterval: 420000,
    refetchIntervalInBackground: true,
  })
}

export const useGetUser = (enabled = true) => {
  const dispatch = useDispatch()
  const getUserLogged = () => {
    dispatch(hideLoader(true))
    return getUser()
  }
  return useQuery([USER_KEYS.GET_USER], getUserLogged, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const useResetPassword = () => {
  return useMutation((userId) => resetPassword(userId))
}

export const useGetUserConfigurations = (enabled) => {
  const dispatch = useDispatch()
  const getUserConfigurations = () => {
    dispatch(hideLoader(true))
    return getUserConfigurationsData()
  }
  return useQuery([USER_KEYS.USER_CONFIGURATIONS], getUserConfigurations, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
  })
}

export const usePostAddSchoolToUser = () => {
  const queryClient = useQueryClient()
  return useMutation(({ schoolCode }) => postAddSchoolToUser({ schoolCode }), {
    onSuccess: ({ user }) => {
      queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => ({
        ...oldData,
        ...user,
      }))
    },
  })
}

export const usePostAddSchoolCode = () => {
  return useMutation(({ schoolCode, userId }) => {
    postAddSchoolCode(schoolCode, userId)
  })
}

export const usePostUserFirstStep = () => {
  return useMutation(postUserFirstStep)
}

export const usePostAddCourseToUser = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ courseId, isEnabledBff }) =>
      postAddCourseToUser({ courseId, isEnabledBff }),
    {
      onSuccess: ({ user }) => {
        queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => ({
          ...oldData,
          ...user,
        }))
      },
    }
  )
}
export const usePostAddClassroomToUser = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ classroomId }) => postAddClassroomToUser({ classroomId }),
    {
      onSuccess: ({ user }) => {
        queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => ({
          ...oldData,
          ...user,
        }))
      },
    }
  )
}

export const useGetUserCourses = ({ enabled }) => {
  const dispatch = useDispatch()
  const getUserCoursesLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getUserCourses()
  }

  return useQuery([USER_KEYS.USER_COURSES], getUserCoursesLoaderHidden, {
    enabled,
  })
}

export const useGetUserClassrooms = ({ enabled }) => {
  const dispatch = useDispatch()
  const getUserClassroomsLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getUserClassrooms()
  }

  return useQuery([USER_KEYS.USER_CLASSROOMS], getUserClassroomsLoaderHidden, {
    enabled,
  })
}

export const usePostResendEmail = () => {
  return useMutation(({ userId }) => postResendEmail({ userId }))
}

export const usePostLegalConditions = () => {
  return useMutation(postLegalConditions)
}

export const usePostUserFeedbackPool = () => {
  const dispatch = useDispatch()
  const postUserFeedbackPoolLoaderHidden = (action) => {
    dispatch(hideLoader(true))
    return postUserFeedbackPool(action)
  }
  return useMutation((action) => postUserFeedbackPoolLoaderHidden(action), {
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
  })
}

export const usePostResetPassword = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ newPassword1, newPassword2 }) =>
      changePassword(newPassword1, newPassword2),
    {
      onSuccess: () => {
        queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => ({
          ...oldData,
          userFlags: {
            ...oldData.userFlags,
            FORCE_RESET_PASSWORD: false,
          },
        }))
      },
    }
  )
}

export const usePatchUser = () => {
  return useMutation(patchUser)
}

export const useUnlinkWithGoogle = () => {
  const queryClient = useQueryClient()
  return useMutation((userId) => unlinkWithGoogle(userId), {
    onSuccess: () => {
      queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => {
        return {
          ...oldData,
          vleIntegrations: [],
        }
      })
    },
  })
}

export const usePostRecoverPassword = () => {
  return useMutation((email) => {
    recoverPassword(email)
  })
}

export const usePatchPromotionTerms = () => {
  const { t } = useTranslation()
  const addFeedback = useSnackBarFeedback()
  const dispatch = useDispatch()
  const patchPromotionTermsLoaderHidden = (userId, value) => {
    dispatch(hideLoader(true))
    return patchUserPromotionTerms(userId, value)
  }
  return useMutation(
    ({ userId, value }) => patchPromotionTermsLoaderHidden(userId, value),
    {
      onError: ({ message }) => {
        addFeedback({
          dataTestId: 'error-feedback-edit-promotion-terms',
          id: 'error-feedback-edit-promotion-terms',
          text: message,
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSettled: () => {
        dispatch(hideLoader(false))
      },
      onSuccess: () => {
        addFeedback({
          dataTestId: 'success-feedback-edit-promotion-terms',
          id: 'success-feedback-edit-promotion-terms',
          text: t('Los cambios se han realizado con éxito'),
          variant: TYPES_ALERTS.SUCCESS,
        })
      },
    }
  )
}

export const useGetSystemConfig = () => {
  const dispatch = useDispatch()
  const getSystemConfigsLoaderHidden = () => {
    dispatch(hideLoader(true))
    return getSystemConfigs()
  }
  return useQuery([USER_KEYS.SYSTEM_CONFIG], getSystemConfigsLoaderHidden, {
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const useLogout = () => {
  return useMutation(() => sendLogout())
}

export const useAcceptEdubookLegalConditions = () => {
  return useMutation(({ userId }) => acceptEdubookLegalConditions({ userId }))
}

export const useAcceptPublisherLegalConditions = () => {
  return useMutation(({ publisherId, userId }) =>
    acceptPublisherLegalConditions({ publisherId, userId })
  )
}

export const usePatchPublisherLegalConditions = () => {
  return useMutation(({ flag, publisherId, userId }) =>
    patchPublisherLegalConditions({ flag, publisherId, userId })
  )
}

export const usePutChangeUserFlag = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ userFlagName, userId, value }) =>
      putChangeUserFlag({ userFlagName, userId, value }),
    {
      onSuccess: (response, data) => {
        const { userFlagName } = data
        queryClient.setQueryData([USER_KEYS.GET_USER], (oldData) => ({
          ...oldData,
          userFlags: {
            ...oldData.userFlags,
            [userFlagName]: STATUS_FLAGS.FALSE,
          },
        }))
      },
    }
  )
}

export const usePublisherLegalConditions = (enabled) => {
  const dispatch = useDispatch()
  const getUserPublisher = () => {
    dispatch(hideLoader(true))
    return getMePublishers()
  }
  return useQuery([USER_KEYS.USER_PUBLISHER], getUserPublisher, {
    enabled,
    onSuccess: () => {
      dispatch(hideLoader(false))
    },
    staleTime: Infinity,
  })
}

export const useGetTutorIaCourses = ({ autonomyId, enabled }) => {
  return useQuery(
    [USER_KEYS.TUTOR_IA_SUBJECTS],
    () => getTutorIaCourses({ autonomyId }),
    {
      enabled,
      staleTime: Infinity,
    }
  )
}

export const usePostTutorIaBookId = () => {
  return useMutation(({ bookId }) => postTutorIaBookId({ bookId }))
}

export const useSetUserPersonalData = () => {
  const queryClient = useQueryClient()
  const addFeedback = useSnackBarFeedback()
  const { t } = useTranslation()
  return useMutation(
    ({ personalData, userId }) =>
      patchUserPersonalData({ personalData, userId }),
    {
      onError: () => {
        addFeedback({
          dataTestId: 'error-feedback-update-student',
          id: 'error-feedback-update-student',
          text: t(
            'Lo sentimos, pero algo ha fallado No se han podido guardar los cambios'
          ),
          variant: TYPES_ALERTS.ERROR,
        })
      },
      onSuccess: (data, variables) => {
        addFeedback({
          dataTestId: 'success-feedback-update-student',
          id: 'success-feedback-update-student',
          text: t('Los cambios se han realizado con éxito'),
          variant: TYPES_ALERTS.SUCCESS,
        })
        queryClient.setQueryData(
          [STUDENT_KEYS.CHILDREN, variables?.userId],
          (oldData) => {
            return {
              ...oldData,
              birthdate: data.birthdate,
              email: data.email,
              locale: data.locale,
              name: data.name,
              surnames: data.surnames,
              username: data.username,
            }
          }
        )
      },
    }
  )
}
